import { useContext } from 'react';
import List from '@pelckmans/elementis/components/list';
import ChapterIcon from '@pelckmans/elementis/icons/chapter';
import { useTranslation } from 'react-i18next';
import ModuleContext from '../../_contexts/ModuleContext';
import { SearchResultSlide, SearchResultSlideSet } from './../api/search-api';
import Pill from '@pelckmans/elementis/components/pill';
import { getFullHierarchyDescriptionOfNode } from '../../../../utils/getNodeDescription';
import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import useLicenses from '../../../../hooks/useLicenses';
import stripHtml from '../../../../utils/wordlist/stripHtml';
import { buildDoceoUrl, buildDoceoUrlForSlide } from '../../../../utils/slideSet';
import IdentityContext from '../../../../contexts/IdentityContext';
import MaterialPills from '../../toc/components/MaterialPills';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../constants/analyticsEvents';
import { useSlideSetAnalytics } from '../../../../hooks/useSlideSetAnalytics';
import AccountContext from '../../../../contexts/AccountContext';

interface Props {
  searchItem: SearchResultSlideSet | SearchResultSlide;
}

function isSearchResultSlide(item: SearchResultSlideSet | SearchResultSlide): item is SearchResultSlide {
  return item.docType === 'slide';
}

function SearchResultSlideSetItem({ searchItem }: Props) {
  const { allModules } = useContext(ModuleContext);
  const {
    account: { provider },
  } = useContext(AccountContext);
  const { t } = useTranslation();
  const { isModuleLicensed } = useLicenses();
  const { captureOpened } = useSlideSetAnalytics();
  const {
    identity: { userId, isTeacherAlike },
  } = useContext(IdentityContext);

  const targetModule = allModules.find(m => m.id === searchItem.moduleId);

  if (!targetModule) return null;
  const licensedModule = isModuleLicensed(targetModule);

  const { hierarchy } = searchItem;

  function buildUrl() {
    if (isSearchResultSlide(searchItem)) {
      return buildDoceoUrlForSlide(
        {
          id: Number(searchItem.id),
          versionId: searchItem.version,
          state: searchItem.state,
        },
        searchItem.slideId,
        searchItem.slideSetVersionSlideGroupId,
        provider,
      );
    }

    return buildDoceoUrl(
      { id: Number(searchItem.id), versionId: searchItem.version, state: searchItem.state },
      provider,
    );
  }

  return (
    <List.Item
      key={searchItem.id}
      as="link"
      title={!licensedModule ? stripHtml(searchItem.name) : searchItem.name}
      subText={isSearchResultSlide(searchItem) ? searchItem.content : undefined}
      disabled={!licensedModule}
      href={buildUrl()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => captureOpened(Number(searchItem.id), ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SEARCH)}
      pillGroupProps={buildGroupProps(
        <>
          <Pill key="scope-pill">{t('PAGES.SEARCH.RESULTS.SLIDE_SET')}</Pill>
          <Pill
            key="module-pill"
            size="sm"
            avatar={
              <Avatar>
                <AvatarImage size="xs" name={targetModule!.niceName} src={targetModule!.styling?.logo || ''} />
              </Avatar>
            }
          >
            {targetModule?.niceName}
          </Pill>
          <Pill key="hierarchy-pill" icon={<ChapterIcon />}>
            {getFullHierarchyDescriptionOfNode(hierarchy, t)}
          </Pill>
          {searchItem.owner === userId && (
            <Pill key="content-type-pill">{t('CONTENT_TYPE_DESCRIPTIONS.OWN_MATERIAL')}</Pill>
          )}
          {(searchItem.shared || searchItem.isAssignment) && searchItem.owner === 'teacher' && (
            <Pill key="content-type-pill">{t('CONTENT_TYPE_DESCRIPTIONS.SHARED_MATERIAL')}</Pill>
          )}
          {isTeacherAlike && searchItem.owner === 'pelckmans' && searchItem.group === 'student' && (
            <Pill key="student-pill">{t('PAGES.TOC_LEVEL.MATERIAL_PILLS.STUDENT')}</Pill>
          )}
          {isTeacherAlike && searchItem.owner === 'pelckmans' && searchItem.group === 'teacher' && (
            <Pill key="teacher-pill">{t('PAGES.TOC_LEVEL.MATERIAL_PILLS.TEACHER')}</Pill>
          )}
          <MaterialPills key="material-pills" isShared={searchItem.shared} isAssignment={searchItem.isAssignment} />
        </>,
      )}
    />
  );
}

export default SearchResultSlideSetItem;
